import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useFollowUpGuard } from '@/pages/FollowUpReport/guards/useFollowUpGuard'
import { ESearchType } from '@/pages/RunReport/types'
import { useLazyGetSubjectBasicInformationQuery } from '@/store/api/Person/Person.query'
import { EReportType } from '@/store/api/Person/Person.types'
import type { getSubjectInfo } from '@/store/api/Person/Person.types'
import { useListPaginatedOfTopicsQuery } from '@/store/api/RunReport/RunReport.query'
import {
  clearNewSearch,
  setFirstStep,
  setReportType,
  setSearchType,
  setSocialMedia,
  setSubjectInfo,
  setUseCases,
} from '@/store/api/RunReport/RunReport.slice'

import { EFollowUpStep } from './types'

const RefreshIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="32"
      cy="32"
      r="24"
      stroke="currentColor"
      strokeWidth="2"
      className="text-dynamicBlue"
      strokeDasharray="4 4"
    />
    <path
      d="M32 16V32L42 42"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutralColor60"
    />
    <path
      d="M44 20L48 16L52 20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-dynamicBlue"
    />
    <path
      d="M48 16V28C48 30.2091 46.2091 32 44 32H38"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-dynamicBlue"
    />
  </svg>
)

const TimelineIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 8V12L15 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-dynamicBlue"
    />
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2"
      className="text-dynamicBlue"
    />
  </svg>
)

const FootprintIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 19L8 15M8 15L12 19M8 15V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-dynamicBlue"
    />
    <circle
      cx="8"
      cy="8"
      r="4"
      stroke="currentColor"
      strokeWidth="2"
      className="text-dynamicBlue"
    />
    <path
      d="M16 11L20 7M20 7L16 3M20 7H14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-dynamicBlue"
    />
  </svg>
)

const CreditIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2"
      className="text-dynamicBlue"
    />
    <path
      d="M12 7V17M9 10H15M9 14H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="text-dynamicBlue"
    />
  </svg>
)

export const NewFollowUpReport = () => {
  useFollowUpGuard(EFollowUpStep.Report)

  const [searchParams] = useSearchParams()
  const reportId = searchParams.get('reportId') ?? ''

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isLoadingSettingFollowUpReport, setIsLoadingSettingFollowUpReport] =
    useState<boolean>(false)

  // Subject basic information
  const [getSubjectBasicInformation] = useLazyGetSubjectBasicInformationQuery()

  // Topics
  const { data: topicsData } = useListPaginatedOfTopicsQuery({
    page: 1,
    pageSize: 1000,
  })

  useEffect(() => {
    // When you render this page, we need to ensure that previous search data is cleared.
    dispatch(clearNewSearch())
  }, [])

  const handleStartFollowUpReport = async () => {
    setIsLoadingSettingFollowUpReport(true)

    try {
      // Get complete report information
      const { info: reportData } = await getSubjectBasicInformation({
        id: reportId,
      }).unwrap()

      dispatch(setReportType({ reportType: reportData.reportType }))
      dispatch(setSearchType(ESearchType.Individual))

      const isAiReport = reportData.reportType === EReportType.Ai

      setFollowUpSubjectInfo(reportData)
      setFollowUpSocialMedia(reportData)
      setFollowUpTopics(reportData)

      if (isAiReport) {
        setFollowUpUseCaseAiReport(reportData)
        navigate(
          `${AppRoutes.newFollowUpReportLegalDisclaimer}?reportId=${reportId}`,
        )
      } else {
        navigate(`${AppRoutes.newFollowUpReportUseCase}?reportId=${reportId}`)
      }
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('feedback.toast.errorOnSearch'))
      navigate(AppRoutes.reports)
    } finally {
      setIsLoadingSettingFollowUpReport(false)
    }
  }

  const setFollowUpUseCaseAiReport = (reportData: getSubjectInfo) => {
    const useCaseId = reportData.useCaseId
    const useCaseName = reportData.useCase

    dispatch(
      setUseCases({
        selectedUseCaseId: useCaseId,
        selectedUseCaseName: useCaseName,
        consentSwitchValue: false,
        isSwitchDisabled: false,
      }),
    )
  }

  const setFollowUpSubjectInfo = (reportData: getSubjectInfo) => {
    dispatch(
      setSubjectInfo({
        firstName: reportData.inputs.name,
        lastName: reportData.inputs.lastName,
        email: reportData.inputs.email || '',
        middleName: reportData.inputs.middleName || '',
        alias: reportData.inputs.nickname || '',
        phoneNumber: reportData.inputs.phone || '',
        college: reportData.inputs.college || '',
        employerName: reportData.inputs.company || '',
        dob: reportData.inputs.ageOrDateOfBirth || '',
        countryLongName: reportData.inputs.country || '',
        countryShortName: reportData.inputs.countryShort || '',
        stateLongName: reportData.inputs.state || '',
        stateShortName: reportData.inputs.stateShort || '',
        cityLongName: reportData.inputs.city || '',
        cityShortName: reportData.inputs.cityShort || '',
        image: reportData.profileImgUrl || '',
      }),
    )
  }

  const setFollowUpSocialMedia = (reportData: getSubjectInfo) => {
    dispatch(
      setSocialMedia({
        facebook:
          reportData.socialMedia.find((item) => item.name === 'Facebook')
            ?.profileLink || '',
        instagram:
          reportData.socialMedia.find((item) => item.name === 'Instagram')
            ?.profileLink || '',
        x:
          reportData.socialMedia.find((item) => item.name === 'Twitter')
            ?.profileLink || '',
        linkedIn:
          reportData.socialMedia.find((item) => item.name === 'LinkedIn')
            ?.profileLink || '',
        tikTok:
          reportData.socialMedia.find((item) => item.name === 'TikTok')
            ?.profileLink || '',
        pinterest:
          reportData.socialMedia.find((item) => item.name === 'Pinterest')
            ?.profileLink || '',
        reddit:
          reportData.socialMedia.find((item) => item.name === 'Reddit')
            ?.profileLink || '',
      }),
    )
  }

  const setFollowUpTopics = (reportData: getSubjectInfo) => {
    const selectedTopics =
      topicsData?.info.list
        .filter((topic) => reportData.inputs.topics.includes(topic.name))
        .map((topic) => topic.name) || []

    dispatch(
      setFirstStep({
        topics: selectedTopics,
      }),
    )
  }

  const features = [
    {
      icon: <TimelineIcon />,
      title: t('dashboard.followUp.intro.timelineTitle'),
      description: t('dashboard.followUp.intro.timelineDescription'),
    },
    {
      icon: <FootprintIcon />,
      title: t('dashboard.followUp.intro.footprintTitle'),
      description: t('dashboard.followUp.intro.footprintDescription'),
    },
    {
      icon: <CreditIcon />,
      title: t('dashboard.followUp.intro.creditTitle'),
      description: t('dashboard.followUp.intro.creditDescription'),
    },
  ]

  return (
    <WeScreen
      isLoading={isLoadingSettingFollowUpReport}
      onClickNextButton={handleStartFollowUpReport}
    >
      <div className="flex flex-col items-center justify-center h-full w-full px-4 py-8">
        <div className="flex flex-col items-center gap-8 max-w-2xl text-center">
          <RefreshIcon />

          <div className="flex flex-col gap-3">
            <h1 className="text-3xl font-medium text-neutralColor tracking-wide">
              {t('dashboard.followUp.intro.title')}
            </h1>
            <p className="text-neutralColor60 text-base leading-relaxed">
              {t('dashboard.followUp.intro.subtitle')}
            </p>
          </div>

          <div className="flex flex-col gap-4 w-full mt-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-5 rounded-xl bg-baseColorHighlight/50  text-left bg-baseColorHighlight"
              >
                <div className="flex-shrink-0 mt-0.5">{feature.icon}</div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-base font-medium text-neutralColor">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-neutralColor60 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-neutralColor40 mt-4">
            {t('dashboard.followUp.intro.disclaimer')}
          </p>
        </div>
      </div>
    </WeScreen>
  )
}
